// PromptGenerator/hooks/usePromptActions.ts
import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../../../config/api';
import { useAuth } from '../../../context/Auth/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { getProjectCharacterRegistry } from '../../../services/characterRegistryService';
import { PromptData } from './useProjectData'; // Import type

// Helper function from PromptLayout.tsx
const shouldShowGenerateImageButton = (promptTitle: string): boolean => {
  if (!promptTitle) return false;
  const titleLower = promptTitle.toLowerCase();
  return titleLower.includes('image') || titleLower.includes('character');
};

// Helper function from PromptLayout.tsx
const extractCharacterNameFromTitle = (title: string): string | null => {
  if (!title) return null;
  const match = title.match(/Character\s+\d+:\s*(?:Physical Appearance:\s*)?([^(]+?)(?:\s*\(|$)/i);
  if (match) return match[1].trim();
  const fallbackMatch = title.match(/Character\s+\d+:\s*(.+?)(?:\s*\(|$)/i);
  return fallbackMatch ? fallbackMatch[1].trim() : null;
};

export const usePromptActions = (
    projectId: string | undefined,
    prompts: PromptData[],
    loadPrompts: () => Promise<void>,
    setPrompts: (p: PromptData[]) => void,
) => {
    const navigate = useNavigate();
    const { token } = useAuth();
    const { showNotification } = useNotification();

    const [showPromptGenerator, setShowPromptGenerator] = useState(false);
    const [promptGeneratorKey, setPromptGeneratorKey] = useState(0);
    const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);
    const [selectedPrompt, setSelectedPrompt] = useState<PromptData | null>(null);
    const [deletingAll, setDeletingAll] = useState(false);
    const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
    
    // --- Navigation Handlers ---
    const handleNavigateToProject = useCallback(() => {
        navigate(`/prompt-generator`);
        setShowPromptGenerator(false);
        setSelectedPrompt(null);
        setSelectedPromptId(null);
    }, [navigate]);

    const handleNewPrompt = useCallback(() => {
        setShowPromptGenerator(true);
        setSelectedPromptId(null);
        setSelectedPrompt(null);
        setPromptGeneratorKey(prev => prev + 1); // Force remount
        
        if (projectId) {
            navigate(`/prompt-generator/${projectId}/prompt-generator`, { replace: true, state: null });
        }
    }, [projectId, navigate]);

    // --- Prompt State & Lifecycle Handlers ---
    const selectPrompt = useCallback(async (promptId: string) => {
        const prompt = prompts.find(p => p.id === promptId);
        if (!prompt || promptId === selectedPromptId) return;

        const isDraft = !prompt.generatedPrompt || prompt.generatedPrompt.trim() === '';
        
        if (isDraft) {
            // Edit draft mode
            setSelectedPrompt(prompt);
            setSelectedPromptId(promptId);
            setShowPromptGenerator(true);
            setPromptGeneratorKey(prev => prev + 1);
            
            navigate(`/prompt-generator/${projectId}/prompt-generator`, {
                state: {
                    editingPrompt: true,
                    promptId: prompt.id,
                    title: prompt.title || 'Untitled Prompt',
                    userIdea: prompt.userSelections?.userIdea || '',
                    attributes: prompt.userSelections?.attributes || ''
                }
            });
        } else {
            // View mode
            setShowPromptGenerator(false);
            setSelectedPrompt(prompt);
            setSelectedPromptId(promptId);
            setPromptGeneratorKey(prev => prev + 1);
        }
    }, [prompts, projectId, selectedPromptId, navigate]);

    const handlePromptCreated = useCallback(async (newPromptId: string) => {
        await loadPrompts(); // Reload the list
        
        try {
            const response = await fetch(getApiUrl(`/prompt-generator/prompt/${newPromptId}`), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' },
            });
            const result = await response.json();
            
            if (result.success) {
                const newPrompt = result.data;
                setSelectedPrompt(newPrompt);
                setSelectedPromptId(newPromptId);
                setShowPromptGenerator(false);
            }
        } catch (error) {
            console.error('Error fetching new prompt:', error);
            setShowPromptGenerator(false);
        }
    }, [loadPrompts]);

    const handleRegeneratePrompt = useCallback(() => {
        if (!selectedPrompt) return;
        
        setShowPromptGenerator(true);
        navigate(`/prompt-generator/${projectId}/prompt-generator`, {
            state: {
                editingPrompt: true,
                promptId: selectedPrompt.id,
                title: selectedPrompt.title || 'Untitled Prompt',
                userIdea: selectedPrompt.userSelections?.userIdea || '',
                attributes: selectedPrompt.userSelections?.attributes || ''
            }
        });
    }, [selectedPrompt, projectId, navigate]);

    // --- Deletion Handlers ---
    const deletePrompt = useCallback(async (promptId: string, promptTitle: string) => {
        if (!confirm(`Are you sure you want to delete "${promptTitle}"? This action cannot be undone.`)) return;

        // Optimistic UI update
        setPrompts(prompts.filter(p => p.id !== promptId));
        if (selectedPromptId === promptId) {
            setSelectedPrompt(null);
            setSelectedPromptId(null);
            setShowPromptGenerator(false);
        }
        
        try {
            const response = await fetch(getApiUrl('/prompt-generator/delete'), {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ promptId })
            });

            if (!response.ok) throw new Error('Failed to delete prompt');
        } catch (error) {
            console.error('Error deleting prompt:', error);
            showNotification({ title: 'Error', message: 'Failed to delete prompt', type: 'error' });
            // Revert UI on failure (needs full implementation if desired)
        }
    }, [selectedPromptId, token, showNotification, setPrompts, prompts]);

    const confirmDeleteAllPrompts = useCallback(async (projectId: string) => {
        if (!projectId || deletingAll) return;
        setDeletingAll(true);
        
        try {
            const response = await fetch(getApiUrl('/prompt-generator/delete-all'), {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ projectId: projectId }),
            });

            const result = await response.json();
            if (!result.success) throw new Error(result.message || 'Failed to delete all prompts');
            
            // Clear all UI state
            setPrompts([]);
            setSelectedPromptId(null);
            setSelectedPrompt(null);
            setShowPromptGenerator(false);
            setShowDeleteAllConfirm(false);

        } catch (error) {
            console.error('Error deleting all prompts:', error);
            showNotification({ title: 'Error', message: error instanceof Error ? error.message : 'Failed to delete all prompts', type: 'error' });
        } finally {
            setDeletingAll(false);
        }
    }, [deletingAll, token, showNotification, setPrompts]);


    // --- Image Generation Handler ---
    const handleGenerateImage = useCallback(async (promptData: PromptData) => {
        if (!promptData || !projectId) return;

        let targetCharacterId = promptData.promptMetadata?.characterId;
        const characterReferences = (promptData.userSelections as any)?.characterMetadata?.characterReferences;
        if (characterReferences?.length > 0) {
            targetCharacterId = characterReferences[0].characterId;
        }

        let characterImageUrl = null;
        if (targetCharacterId) {
            try {
                const characterRegistry = await getProjectCharacterRegistry(projectId);
                characterImageUrl = characterRegistry[targetCharacterId]?.imageUrl;
            } catch (error) {
                console.error('Error loading character registry:', error);
            }
        }

        const queryParams = new URLSearchParams();
        if (characterImageUrl) queryParams.set('charUrl', characterImageUrl);

        queryParams.set('project-id', projectId);
        queryParams.set('prompt-id', promptData.id);
        queryParams.set('initial-prompt', promptData.generatedPrompt);

        if (targetCharacterId) {
            queryParams.append('character-id', targetCharacterId);
            const characterName = extractCharacterNameFromTitle(promptData.title || '') || targetCharacterId;
            queryParams.append('character-name', characterName);
        }

        navigate(`/thumbnail-landing?${queryParams.toString()}`);
    }, [projectId, navigate]);

    return {
        showPromptGenerator, promptGeneratorKey, selectedPromptId, selectedPrompt, deletingAll, showDeleteAllConfirm,
        setShowPromptGenerator, setSelectedPromptId, setSelectedPrompt, setShowDeleteAllConfirm,
        handleNewPrompt, selectPrompt, handlePromptCreated, handleRegeneratePrompt, handleGenerateImage,
        deletePrompt, confirmDeleteAllPrompts, handleNavigateToProject
    };
};